import React, { useState, useEffect } from 'react';
import { differenceInMinutes } from 'date-fns';

import BookOneOffSession from 'coaching/components/CoachingHomeView/BookOneOffSession';
import BookRecurringSession from 'coaching/components/CoachingHomeView/BookRecurringSession';
import BookSessionConfirmationSidebar from 'coaching/components/CoachingHomeView/BookSessionConfirmationSidebar';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CoachAvatarWithSubject from 'coaching/components/CoachAvatarWithSubject';
import CoachingSessionFrequencySelection from 'coaching/components/CoachingHomeView/CoachingSessionFrequencySelection';
import CurrentUser from 'auth/CurrentUser';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import SidebarHeader from 'coaching/components/SidebarHeader';
import Row from 'components/Row';
import SidebarFooter from 'components/SidebarFooter';
import Text from 'components/Text';
import TimezoneHelper from 'lib/timezones';
import ToastActions from 'actions/ToastActions';
import GetPossibleCoachingSessionsForCoachContainer, {
  CoachingSessionSlot,
} from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';
import { createCoachingSession } from 'coaching/actions/CoachingActions';
import useWeekOffset from 'coaching/hooks/useWeekOffset';
import {
  CoachMetadata,
  CoachingSessionTools,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import { userReadableDateFormat } from 'lib/dateTimeFormat';

import { TimeslotFrequencyInput, CoachingEventDefinitionInput } from 'globalTypes';
import GET_COACHING_HOME_DASHBOARD_QUERY from 'coaching/queries/GetCoachingHomeDashboard.gql';
import { CoachingSessionDuration } from 'coaching/CoachingConstants';
import { isNotNullOrUndefined } from 'types/typescript/util';
import { AvailableOrganizationCoachingSessionsContainer } from 'coaching/containers/AvailableOrganizationCoachingSessions';
import { OrganizationTutoringMetadata } from 'utils/coachingOrganizationHelpers';

const userTimezone = TimezoneHelper.userTimezoneDefaultValue ?? 'America/Los_Angeles';

export type SelfScheduleOptions = {
  frequencyOptions: TimeslotFrequencyInput[];
  durationOptions: CoachingSessionDuration[];
};

type BookingSteps = 'frequency-selection' | 'book-session' | 'confirmation';
export type Props = {
  subjectName: string;
  sessionTools: CoachingSessionTools;
  participantUuid: string;
  coach: CoachMetadata;
  onClose: () => void;
  defaultFrequency?: TimeslotFrequencyInput;
  defaultStep?: BookingSteps;
  showFrequencySelect?: boolean;
  organizationTutoring: OrganizationTutoringMetadata;
  selfScheduleOptions: SelfScheduleOptions;
};

function hasMultipleRecurringSessionsFeature() {
  const currentUser = CurrentUser.getUser();
  return Boolean(currentUser && currentUser.hasFeature('add_recurring_sessions.web.dec-21'));
}

export default function BookNewCoachingSessionSidebar({
  sessionTools,
  subjectName,
  coach,
  onClose,
  participantUuid,
  defaultFrequency = TimeslotFrequencyInput.weekly,
  defaultStep = 'frequency-selection',
  showFrequencySelect = true,
  organizationTutoring,
  selfScheduleOptions,
}: Props) {
  const initialFrequency =
    selfScheduleOptions.frequencyOptions.length === 1
      ? selfScheduleOptions.frequencyOptions[0]
      : defaultFrequency;
  const [step, setStep] = useState<BookingSteps>(defaultStep);
  const [frequency, setFrequency] = useState<TimeslotFrequencyInput>(initialFrequency);
  const [loadingEventDefinition, setLoadingEventDefinition] = useState(false);
  const [eventDefinition, setEventDefinition] = useState<CoachingEventDefinitionInput>();
  const [selectedReservation, setSelectedReservation] = useState<CoachingSessionSlot | undefined>();
  const [submitting, setSubmitting] = useState(false);

  // we fetch 6 days at a time for one-off sessions, because the UI allows the user to
  // change the weekOffset. however, for recurring sessions, we fetch 30 days all at once
  // because the user cannot change the weekOffset from the UI.
  const daysToFetch = frequency === 'once' ? 6 : 30;
  const { weekOffset, setWeekOffset, startDateTimeRange, endDateTimeRange } = useWeekOffset(
    daysToFetch
  );

  const eventPrefix = `${coachingHomeEventPrefix}.add_session_sidebar`;
  const eventPrefixWithFrequency =
    frequency === TimeslotFrequencyInput.once
      ? `${eventPrefix}.book_one_off_session`
      : `${eventPrefix}.book_recurring_session`;

  useEffect(() => {
    // reset state if we change the participant/subject/coach
    const firstStep =
      selfScheduleOptions.frequencyOptions.length === 1 ? 'book-session' : defaultStep;
    setStep(firstStep);
    setSelectedReservation(undefined);
    setEventDefinition(undefined);
    setWeekOffset(0);
  }, [participantUuid, sessionTools.subject.uuid, coach.uuid]);

  if (!startDateTimeRange || !endDateTimeRange) {
    return (
      <ErrorReporter error="BookNewCoachingSessionSidebar - tried to render without a dateTimeRange">
        Oops! Looks like something went wrong. Please try refreshing the page.
      </ErrorReporter>
    );
  }

  async function handleSubmit() {
    if (eventDefinition && selectedReservation?.coachUuid) {
      setSubmitting(true);
      await createCoachingSession(
        {
          participantUuid,
          coachUuid: selectedReservation.coachUuid,
          eventDefinition,
        },
        [
          {
            query: GET_COACHING_HOME_DASHBOARD_QUERY,
            variables: {},
          },
        ],
        () => setStep('confirmation')
      );
      setSubmitting(false);
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to book a session without selecting a date and time. Please try again.'
      );
    }
  }

  const coachAvatarComponent = (
    <BoxBody size="xsmall">
      <CoachAvatarWithSubject subjectName={subjectName} coachFullName={coach.name} />
    </BoxBody>
  );

  const AvailabilityEndpoint = organizationTutoring.enrolledInOrganizationTutoring
    ? AvailableOrganizationCoachingSessionsContainer
    : GetPossibleCoachingSessionsForCoachContainer;

  const stepToComponentMap: Record<BookingSteps, React.ReactElement> = {
    'frequency-selection': (
      <>
        {coachAvatarComponent}
        <Divider size="flush" />
        <BoxBody size="xsmall">
          <CoachingSessionFrequencySelection
            data-test="frequency-selection-step"
            eventPrefix={`${eventPrefix}.frequency_selection`}
            interfaceType="buttons"
            selectableFrequencies={selfScheduleOptions.frequencyOptions}
            onSelectFrequency={(frequency: TimeslotFrequencyInput) => {
              setStep('book-session');
              setFrequency(frequency);
            }}
          />
        </BoxBody>
      </>
    ),
    'book-session': (
      <>
        {coachAvatarComponent}
        <Divider size="flush" />
        <AvailabilityEndpoint
          subjectUuid={sessionTools.subject.uuid}
          coachUuid={coach.uuid}
          startDateTimeRange={startDateTimeRange}
          endDateTimeRange={endDateTimeRange}
          frequency={frequency}
          participantUuid={participantUuid}>
          {props => (
            <>
              {showFrequencySelect && selfScheduleOptions.frequencyOptions.length > 1 && (
                <>
                  <BoxBody size="small">
                    {selfScheduleOptions.frequencyOptions.length > 1 && (
                      <CoachingSessionFrequencySelection
                        interfaceType="select"
                        onSelectFrequency={(frequency: TimeslotFrequencyInput) => {
                          setFrequency(frequency);
                          if (frequency !== TimeslotFrequencyInput.once) {
                            // we need to reset the week offset in case the user is coming
                            // from the one-off scheduling UI and they have navigated into
                            // the future
                            setWeekOffset(0);
                          }
                        }}
                        selectableFrequencies={selfScheduleOptions.frequencyOptions}
                        selectedFrequency={frequency}
                      />
                    )}
                  </BoxBody>
                  <Divider size="flush" />
                </>
              )}
              {frequency === TimeslotFrequencyInput.once ? (
                <BookOneOffSession
                  {...props}
                  data-test="book-one-off-session"
                  startDateTimeRange={startDateTimeRange}
                  endDateTimeRange={endDateTimeRange}
                  weekOffset={weekOffset}
                  timezone={coach.timezone ?? userTimezone}
                  sessionSlots={props.sessionSlots?.filter(isNotNullOrUndefined)}
                  onSelectReservation={res => {
                    setSelectedReservation(res?.selectedAvailability);
                    setEventDefinition(res?.eventDefinition);
                  }}
                  selfScheduleOptions={selfScheduleOptions}
                  onWeekForward={() => setWeekOffset(prev => prev + 1)}
                  onWeekBack={() => setWeekOffset(prev => prev - 1)}
                  eventPrefix={eventPrefixWithFrequency}
                />
              ) : (
                <BookRecurringSession
                  {...props}
                  data-test="book-recurring-session"
                  participantUuid={participantUuid}
                  coachUuid={coach.uuid}
                  timezone={coach.timezone ?? userTimezone}
                  frequency={frequency}
                  onSelectReservation={res => {
                    setSelectedReservation(res?.selectedAvailability);
                    setEventDefinition(res?.eventDefinition);
                  }}
                  onLoadEventDefinition={setLoadingEventDefinition}
                  eventPrefix={eventPrefixWithFrequency}
                  selfScheduleOptions={selfScheduleOptions}
                />
              )}
              <BookSessionsFooter
                disabled={!eventDefinition || loadingEventDefinition}
                submitting={submitting}
                onSubmit={handleSubmit}
                eventDefinition={eventDefinition}
                frequency={frequency}
                eventPrefix={eventPrefixWithFrequency}
                hideBillingText={organizationTutoring.enrolledInOrganizationTutoring}
              />
            </>
          )}
        </AvailabilityEndpoint>
      </>
    ),
    confirmation: (
      <BookSessionConfirmationSidebar
        data-test="book-session-confirmation-step"
        streamUuid={sessionTools.streamUuid}
        eventDefinition={eventDefinition}
        eventPrefix={`${eventPrefixWithFrequency}_confirmation`}
        frequency={frequency}
      />
    ),
  };

  return (
    <>
      <SidebarHeader
        title={hasMultipleRecurringSessionsFeature() ? 'Add sessions' : 'Book sessions'}
        eventPrefix={eventPrefixWithFrequency}
        onClose={onClose}
      />
      {stepToComponentMap[step]}
    </>
  );
}

function getBookSessionCTA(frequency: TimeslotFrequencyInput) {
  if (hasMultipleRecurringSessionsFeature()) {
    switch (frequency) {
      case TimeslotFrequencyInput.once:
        return 'Add session';
      case TimeslotFrequencyInput.everyOtherWeek:
        return 'Add biweekly sessions';
      case TimeslotFrequencyInput.weekly:
        return 'Add weekly sessions';
    }
  }

  return 'Book sessions';
}

function BookSessionsFooter({
  disabled,
  submitting,
  onSubmit,
  eventDefinition,
  frequency,
  eventPrefix,
  hideBillingText,
}: {
  disabled: boolean;
  submitting: boolean;
  onSubmit: () => Promise<void> | void;
  eventDefinition?: CoachingEventDefinitionInput;
  frequency: TimeslotFrequencyInput;
  eventPrefix?: string;
  hideBillingText: boolean;
}) {
  return (
    <SidebarFooter>
      <BoxBody size="small">
        {!hideBillingText && (
          <EventExplanation eventDefinition={eventDefinition} frequency={frequency} />
        )}
        <Button
          data-test="book-sessions-button"
          eventKey={eventPrefix && `${eventPrefix}.book_session.click`}
          full
          disabled={disabled}
          loading={submitting}
          onClick={onSubmit}>
          {getBookSessionCTA(frequency)}
        </Button>
      </BoxBody>
    </SidebarFooter>
  );
}

function EventExplanation({
  eventDefinition,
  frequency,
}: {
  eventDefinition?: CoachingEventDefinitionInput;
  frequency: TimeslotFrequencyInput;
}) {
  if (!eventDefinition) return null;

  const duration = differenceInMinutes(eventDefinition.endDateTime, eventDefinition.startDateTime);
  const firstInstanceText = (
    <Text weight="semibold">{userReadableDateFormat(eventDefinition?.startDateTime)}</Text>
  );
  const recurringText = (
    <>
      You'll be billed ${duration} after each session starting {firstInstanceText}.
    </>
  );
  const oneOffText = (
    <>
      Your session will be scheduled for
      <br /> {firstInstanceText}. You will be billed ${duration} after
      <br />
      the session.
    </>
  );
  const frequencyToTextMap: Record<TimeslotFrequencyInput, React.ReactNode> = {
    [TimeslotFrequencyInput.once]: oneOffText,
    [TimeslotFrequencyInput.everyOtherWeek]: recurringText,
    [TimeslotFrequencyInput.weekly]: recurringText,
  };

  return <Row size="small">{frequencyToTextMap[frequency]}</Row>;
}
